import { Box } from '@mui/material';
import type { UtilisationGranularity } from '@timelog/pm-shared';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { CELL_WIDTH_BY_SCALE, PLANNER_HEADER_HEIGHT_PX } from './plannerGeometry';
import { bucketLabel } from './plannerWindow';

import { MicroLabel, MonoText } from '@/components/ui';

export type PlannerHeaderProps = {
  buckets: readonly string[];
  granularity: UtilisationGranularity;
};

/**
 * The period band over the track. Memoized on (buckets, granularity) — the
 * bucket array only changes when the scale does.
 */
export const PlannerHeader = memo(({ buckets, granularity }: PlannerHeaderProps) => {
  const { t } = useTranslation(['pmResources', 'pmCommon']);
  const cellWidth = CELL_WIDTH_BY_SCALE[granularity];
  return (
    <Box sx={{ display: 'flex', flex: 'none' }}>
      {buckets.map((bucket) => {
        const caption = bucketLabel(bucket, granularity, t);
        return (
          // Plain flex, not Stack: the theme's MuiStack default gap would
          // stretch the fixed-height band.
          <Box
            key={bucket}
            data-automation-id="planner-period"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: `${String(cellWidth)}px`,
              height: PLANNER_HEADER_HEIGHT_PX,
              flex: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              rowGap: '1px',
              borderLeft: 'var(--border-width-thin) solid var(--border-subtle)',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            <MicroLabel color="var(--fg-2)">{caption.label}</MicroLabel>
            <MonoText size="label" color="var(--fg-3)">
              {caption.sub}
            </MonoText>
          </Box>
        );
      })}
    </Box>
  );
});

PlannerHeader.displayName = 'PlannerHeader';
